import { useMemo, useContext } from 'react'
import { PracticePlans } from '../constants/profile'
import { AppDataContext } from '../providers/AppData'
import useTranslateMessage from './useTranslateMessage'
import { SearchReviewsFilters } from '../providers/SearchReviews/types'
import { SearchSpecialistsFilters } from '../providers/SearchSpecialists/types'
import { transformToHash } from '../utils/hash'
import { PracticeProfile } from '../types/practice'
import { useHrefLang } from './useHrefLang'
import { useWpaTenant } from './useWpaTenant'
import { getDefaultCountry } from '../constants/global'
import { preloadImageLoader } from '../utils/imageLoaders'

export default function usePracticeProfileMeta({
  profile,
  filters,
  isReviews,
  isTeams
}: {
  profile: PracticeProfile
  filters?: SearchReviewsFilters | SearchSpecialistsFilters
  isReviews?: boolean
  isTeams?: boolean
}) {
  const { language, locale } = useContext(AppDataContext)
  const translate = useTranslateMessage()
  const { isWPA } = useWpaTenant()
  const defaultCountry = getDefaultCountry(locale)
  const host = String(isWPA ? process.env.NEXT_PUBLIC_WPA_HOST : process.env.NEXT_PUBLIC_HOST)
  const isBasic = profile.basic || profile.plan.includes(PracticePlans.UNCLAIMED)
  const hrefLangs = useHrefLang()
  return useMemo(() => {
    const name = profile.name[language]
    const city = profile.address?.city?.[language] || defaultCountry
    const fixedSlug = profile.slug.replace(/_/g, '-')
    const currentPage = filters?.page ?? filters?.more
    const canonicalTransformedToHash = transformToHash({
      search: filters?.search,
      keyword: filters?.keyword,
      sort: filters?.sort
    })
    const preloadImage = preloadImageLoader({ src: profile?.images?.logo, host })
    const canonicalHash = canonicalTransformedToHash ? `#${canonicalTransformedToHash}` : ''
    const title = isReviews
      ? (translate(isBasic ? 'profile.practice.meta.title.basic' : 'profile.practice.meta.title.review', {
          name,
          city
        }) as string)
      : (translate(isBasic ? 'profile.practice.meta.title.basic' : 'profile.practice.meta.title', {
          name,
          city
        }) as string)
    const teamsTitle = translate('profile.practice.meta.title.teams', {
      name,
      city
    })

    const description = isReviews
      ? (translate(isBasic ? 'profile.practice.meta.description.basic' : 'profile.practice.meta.description.review', {
          name,
          city,
          reviews: profile.reviewsTotal
        }) as string)
      : (translate(isBasic ? 'profile.practice.meta.description.basic' : 'profile.practice.meta.description', {
          name,
          city
        }) as string)
    const teamsDescription = translate('profile.practice.meta.description.teams', {
      name,
      city
    })

    const canonicalUrl = `${host}/${locale}/practice/${fixedSlug}${
      currentPage ? `/reviews/page-${currentPage}${canonicalHash}` : ''
    }`

    const teamsCanonicalUrl = `${host}/${locale}/practice/${fixedSlug}${
      currentPage ? `/team/page-${currentPage}${canonicalHash}` : ''
    }`

    return {
      preloadImage,
      title: isTeams ? teamsTitle : title,
      description: isTeams ? teamsDescription : description,
      canonicalUrl: isTeams ? teamsCanonicalUrl : canonicalUrl,
      language,
      hrefLangs,
      noindex: profile.demo
    }
  }, [profile, defaultCountry, filters, host, hrefLangs, isBasic, isReviews, isTeams, language, locale, translate])
}
